import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDate, getInitials, roleColors, roleLabels } from '../lib/utils';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Clock,
} from 'lucide-react';
import { ImportExportManager } from '../components/ImportExportManager';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_USER_COLUMNS = [
  { id: 'usuario',         label: 'Usuario',         visible: true, order: 0 },
  { id: 'contacto',        label: 'Contacto',         visible: true, order: 1 },
  { id: 'rol',             label: 'Rol',              visible: true, order: 2 },
  { id: 'estado',          label: 'Estado',           visible: true, order: 3 },
  { id: 'fecha_registro',  label: 'Fecha registro',   visible: true, order: 4 },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UsersPage = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pendingReviewOnly, setPendingReviewOnly] = useState(() => searchParams.get('pending_review') === 'true');
  const [approvingUserId, setApprovingUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'asesor',
    phone: '',
    aseguradora: '',
    aseguradoras: []
  });
  const [managedAgents, setManagedAgents] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);

  // Column customizer
  const [userColumns, setUserColumns] = useState(DEFAULT_USER_COLUMNS);

  // Bulk selection
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [bulkRoleOpen, setBulkRoleOpen] = useState(false);
  const [bulkPasswordOpen, setBulkPasswordOpen] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkRole, setBulkRole] = useState('agente');
  const [bulkPassword, setBulkPassword] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [generatingPassword, setGeneratingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      const agentLike = response.data.filter(u =>
        ['agente', 'asistente', 'aspirante'].includes(u.role) && u.is_active
      );
      setAvailableAgents(agentLike);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (updateData.password) {
          updateData.force_password_change = true;
        } else {
          delete updateData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        if ((formData.role === 'developer' || formData.role === 'asistente') && isAdmin) {
          await axios.put(`${API}/users/${editingUser.id}/managed-agents`, { managed_agents: managedAgents });
        }
        setUsers(prev => prev.map(u =>
          u.id === editingUser.id ? { ...u, ...updateData, managed_agents: managedAgents } : u
        ));
        toast.success('Usuario actualizado');
        setDialogOpen(false);
        resetForm();
      } else {
        try {
          const payload = { ...formData };
          if (payload.role === 'aspirante' && payload.aseguradora) {
            payload.aseguradoras = [payload.aseguradora];
          }
          delete payload.aseguradora;
          const response = await axios.post(`${API}/users`, payload);
          setUsers(prev => [...prev, response.data]);
          setDialogOpen(false);
          resetForm();
          toast.success('Usuario creado exitosamente');
        } catch (error) {
          const msg = error.response?.data?.detail ||
            (Array.isArray(error.response?.data) ? error.response.data.map(e => e.msg).join(', ') : 'Error al crear usuario');
          toast.error(msg);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      phone: user.phone || '',
      aseguradora: '',
      aseguradoras: user.aseguradoras || []
    });
    setManagedAgents(user.managed_agents || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleApproveReactivation = async (user) => {
    setApprovingUserId(user.id);
    try {
      await axios.put(`${API}/users/${user.id}/approve-reactivation`);
      toast.success(`Cuenta de ${user.full_name} reactivada`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar la reactivación');
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleToggleActive = async (user) => {
    const activating = !user.is_active;
    try {
      const res = await axios.put(`${API}/users/${user.id}`, { is_active: activating });
      if (activating && res.data?.email_sent) {
        toast.success(`Usuario activado. Se envió el correo de acceso a ${res.data.email}.`);
      } else if (activating) {
        toast.success('Usuario activado.');
      } else {
        toast.success('Usuario desactivado.');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar usuario');
    }
  };

  const handleGenerateTempPassword = async () => {
    setGeneratingPassword(true);
    try {
      const res = await axios.get(`${API}/users/generate-temp-password`);
      setFormData(prev => ({ ...prev, password: res.data.password }));
    } catch (error) {
      toast.error('Error al generar contraseña');
    } finally {
      setGeneratingPassword(false);
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'asesor',
      phone: '',
      aseguradora: '',
      aseguradoras: []
    });
    setManagedAgents([]);
  };

  const handleBulkUpdateUsers = async (type) => {
    const userIds = [...selectedUserIds];
    if (!userIds.length) return;
    if (type === 'password' && !bulkPassword.trim()) {
      toast.error('Ingresa una contraseña');
      return;
    }
    setBulkProcessing(true);
    try {
      const payload = { user_ids: userIds };
      if (type === 'role') payload.role = bulkRole;
      if (type === 'password') payload.password = bulkPassword;
      const res = await axios.post(`${API}/users/bulk-update`, payload);
      toast.success(`${res.data.updated} usuario(s) actualizados`);
      if (type === 'role') setBulkRoleOpen(false);
      if (type === 'password') setBulkPasswordOpen(false);
      setSelectedUserIds(new Set());
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkDeleteUsers = async () => {
    const userIds = [...selectedUserIds];
    if (!userIds.length) return;
    setBulkProcessing(true);
    try {
      await axios.delete(`${API}/users/bulk-delete`, { data: { user_ids: userIds } });
      toast.success(`${userIds.length} usuario(s) eliminados`);
      setBulkDeleteConfirmOpen(false);
      setSelectedUserIds(new Set());
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setBulkProcessing(false);
    }
  };

  const visibleUserCols = userColumns
    .filter(c => c.visible)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(c => c.id);

  const visibleUserColObjs = userColumns
    .filter(c => c.visible)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const [sortField, setSortField] = useState('full_name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline-block flex-shrink-0" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-[#1a365d] inline-block flex-shrink-0" />
      : <ArrowDown className="h-3 w-3 ml-1 text-[#1a365d] inline-block flex-shrink-0" />;
  };

  const filteredUsers = users.filter(user => {
    const name = (user.full_name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const q = search.toLowerCase();
    const matchesSearch = name.includes(q) || email.includes(q);
    const matchesRole = roleFilter === 'all' || (user.role || '') === roleFilter;
    const matchesPendingReview = !pendingReviewOnly || user.pending_review;
    return matchesSearch && matchesRole && matchesPendingReview;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const av = (a[sortField] ?? '').toString().toLowerCase();
    const bv = (b[sortField] ?? '').toString().toLowerCase();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Usuarios</h1>
          <p className="text-muted-foreground">Gestión de usuarios y permisos</p>
        </div>
        <div className="flex gap-2">
          <ColumnCustomizer
            columns={userColumns}
            storageKey="users"
            onColumnsChange={setUserColumns}
          />
          <ImportExportManager
            module="users"
            apiUrl={API}
            data={users}
            onSuccess={fetchUsers}
          />
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2d8659] hover:bg-[#2d8659]/90" data-testid="new-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Modifica los datos del usuario' : 'Crea un nuevo usuario en el sistema'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="full_name">Nombre completo *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Nombre completo"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+52 55 1234 5678"
                    data-testid="user-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Rol *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue placeholder="Selecciona un rol" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="master">Master</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="agente">Agente</SelectItem>
                      <SelectItem value="asistente">Asistente</SelectItem>
                      <SelectItem value="aspirante">Aspirante</SelectItem>
                      <SelectItem value="asesor">Asesor</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="developer">Desarrollador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(formData.role === 'developer' || formData.role === 'asistente') && editingUser && isAdmin && (
                  <div className="space-y-2">
                    <Label>{formData.role === 'asistente' ? 'Carteras asignadas' : 'Agentes a cargo'}</Label>
                    <p className="text-xs text-muted-foreground">
                      {formData.role === 'asistente'
                        ? 'Selecciona los agentes cuya cartera podrá ver y gestionar este Asistente'
                        : 'Selecciona los agentes/asistentes que este Desarrollador podrá ver y gestionar.'}
                    </p>
                    <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                      {availableAgents.length === 0 && (
                        <p className="text-xs text-muted-foreground">No hay agentes disponibles.</p>
                      )}
                      {[...(formData.role === 'asistente'
                          ? [...availableAgents, ...users.filter(u => u.role === 'admin' && u.is_active && !availableAgents.some(a => a.id === u.id))]
                          : availableAgents
                        )].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es')).map(agent => (
                        <label key={agent.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={managedAgents.includes(agent.id)}
                            onChange={e => {
                              if (e.target.checked) setManagedAgents(prev => [...prev, agent.id]);
                              else setManagedAgents(prev => prev.filter(id => id !== agent.id));
                            }}
                          />
                          <span className="text-sm">{agent.full_name}</span>
                          <span className="text-xs text-muted-foreground">({agent.role})</span>
                        </label>
                      ))}
                    </div>
                    {managedAgents.length > 0 && (
                      <p className="text-xs text-muted-foreground">{managedAgents.length} agente(s) seleccionado(s)</p>
                    )}
                  </div>
                )}
                {(formData.role === 'developer' || formData.role === 'asistente') && editingUser && isAdmin && (
                  <div className="space-y-2">
                    <Label>Aseguradoras con acceso</Label>
                    <p className="text-xs text-muted-foreground">
                      Determina qué información del chatbot puede consultar este usuario.
                    </p>
                    <div className="border rounded-md p-2 space-y-1">
                      {['Allianz', 'Insignia Life', 'Quálitas'].map(aseg => (
                        <label key={aseg} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={formData.aseguradoras.includes(aseg)}
                            onChange={e => {
                              if (e.target.checked) {
                                setFormData(prev => ({ ...prev, aseguradoras: [...prev.aseguradoras, aseg] }));
                              } else {
                                setFormData(prev => ({ ...prev, aseguradoras: prev.aseguradoras.filter(a => a !== aseg) }));
                              }
                            }}
                          />
                          <span className="text-sm">{aseg}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {formData.role === 'aspirante' && (
                  <div className="space-y-2">
                    <Label htmlFor="aseguradora">Aseguradora *</Label>
                    <select
                      id="aseguradora"
                      value={formData.aseguradora}
                      onChange={(e) => setFormData({ ...formData, aseguradora: e.target.value })}
                      required
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <option value="">Seleccionar aseguradora</option>
                      <option value="Allianz">Allianz</option>
                      <option value="Insignia Life">Insignia Life</option>
                      <option value="Qualitas">Quálitas</option>
                    </select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="password">
                    {editingUser ? 'Nueva contraseña provisional (opcional)' : 'Contraseña *'}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="password"
                      type={editingUser ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={editingUser ? 'Dejar vacío para no cambiar' : '••••••••'}
                      required={!editingUser}
                      data-testid="user-password-input"
                      className="flex-1"
                    />
                    {editingUser && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleGenerateTempPassword}
                        disabled={generatingPassword}
                        data-testid="generate-temp-password-btn"
                      >
                        Generar automática
                      </Button>
                    )}
                  </div>
                  {editingUser && (
                    <p className="text-xs text-muted-foreground">
                      Si llenas este campo, el usuario deberá cambiar su contraseña al siguiente inicio de sesión.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#1a365d] hover:bg-[#1a365d]/90" data-testid="user-submit-btn">
                  {editingUser ? 'Actualizar' : 'Crear Usuario'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar usuarios..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="user-search"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="role-filter">
                <Shield className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrar por rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                <SelectItem value="master">Master</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="agente">Agente</SelectItem>
                <SelectItem value="asistente">Asistente</SelectItem>
                <SelectItem value="aspirante">Aspirante</SelectItem>
                <SelectItem value="asesor">Asesor</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="developer">Desarrollador</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={pendingReviewOnly ? 'default' : 'outline'}
              className={pendingReviewOnly ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
              onClick={() => setPendingReviewOnly(v => !v)}
              data-testid="pending-review-filter"
            >
              <Clock className="h-4 w-4 mr-2" />
              Pendientes de revisión
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedUserIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-[#1a365d]/10 border border-[#1a365d]/20 rounded-lg">
          <span className="text-sm font-medium text-[#1a365d] dark:text-blue-300">
            {selectedUserIds.size} usuario(s) seleccionado(s)
          </span>
          <div className="flex flex-wrap gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={() => { setBulkRole('agente'); setBulkRoleOpen(true); }}>
              Cambiar rol
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkPassword(''); setBulkPasswordOpen(true); }}>
              Contraseña temporal
            </Button>
            {currentUser?.role === 'admin' && (
              <Button size="sm" variant="destructive" onClick={() => setBulkDeleteConfirmOpen(true)}>
                <Trash2 className="h-3 w-3 mr-1" />
                Eliminar ({selectedUserIds.size})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelectedUserIds(new Set())}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                    checked={sortedUsers.length > 0 && sortedUsers.every(u => selectedUserIds.has(u.id))}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedUserIds(prev => new Set([...prev, ...sortedUsers.map(u => u.id)]));
                      } else {
                        setSelectedUserIds(prev => {
                          const next = new Set(prev);
                          sortedUsers.forEach(u => next.delete(u.id));
                          return next;
                        });
                      }
                    }}
                  />
                </TableHead>
                {visibleUserColObjs.map(col => {
                  if (col.id === 'usuario') return <TableHead key="usuario" className="cursor-pointer select-none" onClick={() => handleSort('full_name')}>Usuario <SortIcon field="full_name" /></TableHead>;
                  if (col.id === 'contacto') return <TableHead key="contacto">Contacto</TableHead>;
                  if (col.id === 'rol') return <TableHead key="rol" className="cursor-pointer select-none" onClick={() => handleSort('role')}>Rol <SortIcon field="role" /></TableHead>;
                  if (col.id === 'estado') return <TableHead key="estado">Estado</TableHead>;
                  if (col.id === 'fecha_registro') return <TableHead key="fecha_registro" className="cursor-pointer select-none" onClick={() => handleSort('created_at')}>Fecha registro <SortIcon field="created_at" /></TableHead>;
                  return null;
                })}
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visibleUserCols.length + 2} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d] mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : sortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleUserCols.length + 2} className="text-center py-8 text-muted-foreground">
                    No se encontraron usuarios
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.map((user) => (
                  <TableRow key={user.id} className={`data-row ${selectedUserIds.has(user.id) ? 'bg-[#1a365d]/5' : ''}`} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        checked={selectedUserIds.has(user.id)}
                        onChange={e => {
                          setSelectedUserIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(user.id);
                            else next.delete(user.id);
                            return next;
                          });
                        }}
                      />
                    </TableCell>
                    {visibleUserColObjs.map(col => {
                      if (col.id === 'usuario') return (
                        <TableCell key="usuario">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={user.avatar_url} />
                              <AvatarFallback className="bg-[#1a365d] text-white text-xs">
                                {getInitials(user.full_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="font-medium">{user.full_name}</div>
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'contacto') return (
                        <TableCell key="contacto">
                          <div className="text-sm">{user.email}</div>
                          <div className="text-xs text-muted-foreground">{user.phone || '-'}</div>
                        </TableCell>
                      );
                      if (col.id === 'rol') return (
                        <TableCell key="rol">
                          <Badge className={`${roleColors[user.role]} border-0`}>
                            {roleLabels[user.role]}
                          </Badge>
                        </TableCell>
                      );
                      if (col.id === 'estado') return (
                        <TableCell key="estado">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={user.is_active}
                                onCheckedChange={() => handleToggleActive(user)}
                              />
                              {user.is_active ? (
                                <UserCheck className="h-4 w-4 text-green-500" />
                              ) : (
                                <UserX className="h-4 w-4 text-red-500" />
                              )}
                            </div>
                            {user.account_suspended && (
                              <Badge
                                className={`border-0 w-fit ${user.pending_review
                                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}
                              >
                                {user.pending_review ? 'Pendiente de revisión' : 'Suspendido'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'fecha_registro') return (
                        <TableCell key="fecha_registro" className="text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                      );
                      return null;
                    })}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(user)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          {user.pending_review && (
                            <DropdownMenuItem
                              onClick={() => handleApproveReactivation(user)}
                              disabled={approvingUserId === user.id}
                              className="text-green-600"
                            >
                              <UserCheck className="h-4 w-4 mr-2" />
                              {approvingUserId === user.id ? 'Aprobando...' : 'Aprobar reactivación'}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Modal: Bulk cambiar rol ── */}
      <Dialog open={bulkRoleOpen} onOpenChange={setBulkRoleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cambiar rol</DialogTitle>
            <DialogDescription>
              Asigna un nuevo rol a los {selectedUserIds.size} usuario(s) seleccionados.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bulk-role">Nuevo rol</Label>
              <Select value={bulkRole} onValueChange={setBulkRole}>
                <SelectTrigger id="bulk-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">Master</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="agente">Agente</SelectItem>
                  <SelectItem value="asistente">Asistente</SelectItem>
                  <SelectItem value="aspirante">Aspirante</SelectItem>
                  <SelectItem value="asesor">Asesor</SelectItem>
                  <SelectItem value="viewer">Viewer (solo lectura)</SelectItem>
                  <SelectItem value="developer">Desarrollador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkRoleOpen(false)} disabled={bulkProcessing}>Cancelar</Button>
            <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90" onClick={() => handleBulkUpdateUsers('role')} disabled={bulkProcessing}>
              {bulkProcessing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
              Aplicar a {selectedUserIds.size} usuario(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Bulk contraseña temporal ── */}
      <Dialog open={bulkPasswordOpen} onOpenChange={setBulkPasswordOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Contraseña temporal</DialogTitle>
            <DialogDescription>
              Asigna una contraseña provisional a los {selectedUserIds.size} usuario(s) seleccionados. Deberán cambiarla en su próximo inicio de sesión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bulk-password">Nueva contraseña provisional *</Label>
              <Input
                id="bulk-password"
                type="password"
                value={bulkPassword}
                onChange={e => setBulkPassword(e.target.value)}
                placeholder="Contraseña temporal"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkPasswordOpen(false)} disabled={bulkProcessing}>Cancelar</Button>
            <Button className="bg-[#1a365d] hover:bg-[#1a365d]/90" onClick={() => handleBulkUpdateUsers('password')} disabled={bulkProcessing}>
              {bulkProcessing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
              Aplicar a {selectedUserIds.size} usuario(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Modal: Bulk eliminar confirmación ── */}
      <Dialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">¿Eliminar {selectedUserIds.size} usuario(s)?</DialogTitle>
            <DialogDescription>
              Esta acción eliminará permanentemente los usuarios seleccionados y sus agentes vinculados. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(false)} disabled={bulkProcessing}>Cancelar</Button>
            <Button variant="destructive" onClick={handleBulkDeleteUsers} disabled={bulkProcessing}>
              {bulkProcessing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />}
              Sí, eliminar {selectedUserIds.size} usuario(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
